import { Link } from '@remix-run/react';
import clsx from 'clsx';

const baseStyles = {
    solid: 'inline-flex justify-center rounded-md shadow-sm focus:outline-hidden',
    outline: 'inline-flex justify-center rounded-md border focus:outline-hidden'
};

const variantStyles = {
    solid: {
        slate:
            'bg-slate-900 text-white hover:bg-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 active:bg-slate-700 active:text-white/80 disabled:opacity-30 disabled:hover:bg-slate-900',
        blue:
            'bg-crispybluedark-700 text-white hover:bg-crispybluedark-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crispybluedark-700 active:bg-crispyblue-700 active:text-white/80 disabled:opacity-30 disabled:hover:bg-crispybluedark-700',
        white:
            'bg-white text-crispybluedark-700 hover:text-crispyblue-700 focus-visible:text-crispyblue-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:bg-crispyblue-50 active:text-crispyblue-900/80 disabled:opacity-40 disabled:hover:text-crispybluedark-700'
    },
    outline: {
        slate:
            'border-slate-200 text-slate-900 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600 active:border-slate-200 active:bg-slate-50 active:text-slate-900/70 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-transparent',
        blue:
            'border-crispyblue-300 text-crispybluedark-700 hover:border-crispyblue-400 hover:bg-crispyblue-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crispybluedark-700 active:text-crispybluedark-700/70 disabled:opacity-40 disabled:hover:border-crispyblue-300 disabled:hover:bg-transparent',
        white:
            'border-white text-white hover:bg-crispybluedark-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crispybluedark-700 active:text-white/70 disabled:opacity-40 disabled:hover:border-crispyblue-300 disabled:hover:bg-transparent'
    }
};

const sizeStyles = {
    solid: {
        normal: 'py-1 px-4 text-base font-semibold tracking-tight',
        big: 'py-4 px-8 text-2xl font-bold'
    },
    outline: {
        normal: 'py-[calc(--spacing(1)-1px)] px-[calc(--spacing(4)-1px)] text-base font-semibold tracking-tight',
        big: 'py-[calc(--spacing(1)-1px)] px-[calc(--spacing(4)-1px)] text-base font-semibold tracking-tight' // TODO
    }
};

type ButtonProps =
    & ({
        variant?: 'solid';
        color?: keyof typeof variantStyles.solid;
        size?: keyof typeof sizeStyles.solid;
    } | {
        variant: 'outline';
        color?: keyof typeof variantStyles.outline;
        size?: keyof typeof sizeStyles.outline;
    })
    & (
        | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'color'>
        | (Omit<React.ComponentPropsWithoutRef<'button'>, 'color'> & {
            to?: undefined;
        })
    );

export function Button({ className, ...props }: ButtonProps) {
    props.variant ??= 'solid';
    props.color ??= 'slate';
    props.size ??= 'normal';

    className = clsx(
        baseStyles[props.variant],
        sizeStyles[props.variant][props.size],
        props.variant === 'outline'
            ? variantStyles.outline[props.color]
            : props.variant === 'solid'
            ? variantStyles.solid[props.color]
            : undefined,
        className
    );

    if (props.to === undefined) {
        return <button className={className} {...props} />;
    }
    return <Link className={className} {...props} />;
}
